import { NavigateFunction } from 'react-router-dom'

import { AppInstanceInfo, AppVersionsDialogState } from 'common/types/application'
import { InterfaceState } from 'store/interface'
import { ModelState } from 'store/model'
import { DataToCompare } from 'pages/appConsolidation/types'
import { AnalysisResponse } from 'common/types/applicationAnalysis'
import { MyDepartmentTeam } from 'common/types/team'
import { Notification } from 'common/types/notifications'

export enum AppActionTypes {
  OPEN_APP = '@@app/OPEN',
  OPEN_APP_ON_NEW_INSTANCE = '@@app/OPEN_APP_ON_NEW_INSTANCE',
  CREATE_VERSION_FROM_CLOSED_APP = '@@app/CREATE_VERSION_FROM_CLOSED_APP',
  CLOSE_APP = '@@app/CLOSE',
  SET_OPEN_APP = '@@app/SET_OPEN_APP',
  RESET_APP_INFO = '@@app/RESET_APP_INFO',
  SAVE_APP = '@@app/SAVE_APP',
  SAVE_APP_IN_WORKSPACE_OR_TEAM = '@@app/SAVE_APP_IN_WORKSPACE_OR_TEAM',
  SAVE_APP_AS_NEW_VERSION = '@@app/SAVE_APP_AS_NEW_VERSION',
  SET_SAVING_APP = '@@app/SET_SAVING_APP',
  SET_APP_HAS_UNSAVED_CHANGES = '@@app/SET_APP_HAS_UNSAVED_CHANGES',
  SHOW_CREATE_APP_DIALOG = '@@app/SHOW_CREATE_APP_DIALOG',
  CREATE_APP = '@@app/CREATE_APP',
  SET_APP_INFO = '@@app/SET_APP_INFO',
  UPDATE_DEFAULT_INTERFACE_ID = '@@app/UPDATE_DEFAULT_INTERFACE_ID',
  APP_RELOADED = '@@app/APP_RELOADED',
  RELOAD_WITH_MY_VENV = '@@app/RELOAD_WITH_MY_VENV',
  DOWNLOAD_EXAMPLE = '@@app/DOWNLOAD_EXAMPLE',
  CHANGE_APP_VERSION = '@@app/CHANGE_APP_VERSION',
  UPDATE_APP_VERSION_PROPERTIES = '@@app/UPDATE_APP_VERSION_PROPERTIES',
  SET_CHANGING_APP_VERSION = '@@app/SET_CHANGING_APP_VERSION',
  SET_CREATING_APP_VERSION = '@@app/SET_CREATING_APP_VERSION',
  HAS_PROCESS_DELAYED = '@@app/HAS_PROCESS_DELAYED',
  CHECK_HAS_ACTIVE_PROCESS = '@@app/CHECK_HAS_ACTIVE_PROCESS',
  SET_HAS_ACTIVE_PROCESS = '@@app/SET_HAS_ACTIVE_PROCESS',
  SET_TASKS_BOARD_VIEW = '@@app/SET_TASKS_BOARD_VIEW',
  SHOW_APP_VERSIONS_DIALOG = '@@app/SHOW_APP_VERSIONS_DIALOG',
  REFRESH_SCENARIOS_TABLE = '@@app/REFRESH_SCENARIOS_TABLE',
  ADD_BROADCAST_CHANNEL = '@@app/ADD_BROADCAST_CHANNEL',
  REMOVE_BROADCAST_CHANNEL = '@@app/REMOVE_BROADCAST_CHANNEL',
  SHOW_UNREAD_NOTIFICATIONS = '@@app/SHOW_UNREAD_NOTIFICATIONS',
  OPEN_NOTIFICATIONS = '@@app/OPEN_NOTIFICATIONS',
  SET_NOTIFICATIONS_BELL_MOUNTED = '@@app/SET_NOTIFICATIONS_BELL_MOUNTED',
  SET_SAVING_APP_WITH_INTERFACE_EDIT_MODE = '@@app/SAVING_APP_WITH_INTERFACE_EDIT_MODE',
  SET_OPEN_SAVE_APP_DIALOG_FROM_INTERFACE = '@@app/SET_OPEN_SAVE_APP_DIALOG_FROM_INTERFACE',
  SET_OPEN_APP_AFTER_SAVE = '@@app/SET_OPEN_APP_AFTER_SAVE',
  GET_APP_CONSOLIDATION_DIFFERENCES = '@@app/GET_APP_CONSOLIDATION_DIFFERENCES',
  SET_WAITING = '@@app/SET_WAITING',
  CLOSE_APP_FROM_OTHER_TAB = '@@app/CLOSE_APP_FROM_OTHER_TAB',
  SET_ANALYSIS_RESULT = '@@app/SET_ANALYSIS_RESULT',
  CLEAN_ANALYSIS_RESULT = '@@app/CLEAN_ANALYSIS_RESULT',
  SET_OPENING_APP_NAME = '@@app/SET_OPENING_APP_NAME',
  APP_SAVED_FROM_OPEN_APP = '@@app/APP_SAVED_FROM_OPEN_APP',
  CLOSING_APP = '@@app/CLOSING_APP',
  SET_APP_HAS_DOCUMENTATION = '@@app/SET_APP_HAS_DOCUMENTATION',
  SET_CURRENT_TEAM_TAB = '@@app/SET_CURRENT_TEAM_TAB',
  SET_COMMON_TAB_NAME = '@@app/SET_COMMON_TAB_NAME',
  SET_CURRENT_APP_FOLDER = '@@app/SET_CURRENT_APP_FOLDER',
  SAVE_APP_VERSION_IN_ANOTHER_APP = '@@app/SAVE_APP_VERSION_IN_ANOTHER_APP',
  CANCEL_OPEN_APP = '@@app/CANCEL_OPEN_APP',
  CANCEL_OPEN_APP_SUCCESS = '@@app/CANCEL_OPEN_APP_SUCCESS',
  CANCELING_OPEN_APP = '@@app/CANCELING_OPEN_APP',
  FORCE_STOP_AND_REOPEN = '@@app/FORCE_STOP_AND_REOPEN',
  SET_ASSETS_RECOVERY_DIALOG = '@@app/SET_ASSETS_RECOVERY_DIALOG'
}

/** How the task board is drawn. */
export type TaskViewMode = 'table' | 'timeline'

export interface TasksBoardState {
  processId?: string
  view?: TaskViewMode
}

export interface AssetsRecoveryDialogState {
  versionPath?: string
}

export interface AppState {
  readonly info?: AppInstanceInfo
  readonly hasUnsavedChanges?: boolean
  readonly saving?: Boolean
  readonly model: ModelState
  readonly showCreateAppDialog?: Boolean
  readonly appReloaded?: Boolean
  readonly hasProcessDelayed?: Boolean
  /**
   * Whether the user is in an active process of the open application, as of the
   * last time anything happened in one. Undefined until it is first checked, so
   * readers fall back to what opening the app reported.
   */
  readonly hasActiveProcess?: boolean
  /**
   * How the task board was left: which process was being looked at, and whether
   * as a table or a timeline.
   *
   * Kept in the store and not in the board, because the board unmounts every
   * time somebody follows a task into an interface — and coming back to the
   * first process of the list, in table view, is coming back to somebody else's
   * screen. Scoped to the open application: another application has other
   * processes entirely.
   */
  readonly tasksBoard?: TasksBoardState
  readonly interface: InterfaceState
  readonly changingAppVersion?: Boolean
  readonly creatingAppVersion?: Boolean
  readonly appVersionsDialog?: AppVersionsDialogState
  readonly refreshScenariosTableCounter?: number
  readonly broadcastChannel?: BroadcastChannel
  readonly showUnreadNotifications?: boolean
  readonly openNotifications?: OpenNotificationsRequest
  // Pages without the top bar (an interface shared by link) have no bell to hand requests to.
  readonly notificationsBellMounted?: boolean
  readonly savingAppWithInterfaceEditMode?: boolean
  readonly openSaveAppDialogFromInterface?: boolean
  readonly openAppAfterSave?: {
    open: boolean
    openInNewInstance: boolean
    modelPath: string
    save?: boolean
  }
  readonly appConsolidationDifferences?: DataToCompare
  readonly waiting?: boolean
  readonly applicationAnalysisResult?: AnalysisResponse
  readonly openingAppName?: string
  readonly appSavedFromOpenApp?: boolean
  readonly closingApp?: boolean
  readonly currentTeamTab?: MyDepartmentTeam
  readonly commonTabName?: string
  readonly currentAppFolder?: string
  readonly cancelingOpenApp?: boolean
  readonly assetsRecoveryDialog?: AssetsRecoveryDialogState | null
}

export interface OpenAppParams {
  navigate: NavigateFunction
  fullPath: string
  openAsReadOnly?: boolean
  version?: string
  resources?: string
  forceNewInstance?: boolean
  save?: boolean
  saveMsg?: string
  interface?: string
  redirectTo?: string
  attemptRecovery?: boolean
}

/** Asks the bell to open its panel, or the composer, from elsewhere (the interface widget). */
export interface OpenNotificationsRequest {
  view: 'inbox' | 'compose'
  notification?: Notification
}
